import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  Chip,
  CircularProgress,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { RootState } from '../../store';
import { eventService } from '../../services/eventService';
import { getProductsByEvent, Product } from '../../services/dashboardProductService';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface ProductWithEvent extends Product {
  eventTitle: string;
  eventSlug: string;
}

const AllProductsPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [products, setProducts] = useState<ProductWithEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'low_stock' | 'out_of_stock'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadAllProducts();
  }, []);

  const loadAllProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      // Buscar eventos do usuário
      const eventsResponse = await eventService.getEvents({ parishId: user?.parishId });
      
      if (!eventsResponse.success || !eventsResponse.data) {
        setError('Erro ao carregar eventos');
        return;
      }

      // Buscar produtos de todos os eventos
      const allProducts: ProductWithEvent[] = [];
      
      for (const event of eventsResponse.data) {
        try {
          const productsResponse = await getProductsByEvent(event._id);
          if (productsResponse.success && productsResponse.data) {
            const productsWithEvent = productsResponse.data.map((product: Product) => ({
              ...product,
              eventTitle: event.title,
              eventSlug: event.slug
            }));
            allProducts.push(...productsWithEvent);
          }
        } catch (error) {
          console.error(`Erro ao carregar produtos do evento ${event.title}:`, error);
        }
      }

      setProducts(allProducts);
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
      setError('Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  };

  const getStockStatus = (quantity: number) => {
    if (quantity === 0) {
      return <Chip icon={<WarningIcon />} label="Sem estoque" color="error" size="small" />;
    } else if (quantity <= 5) {
      return <Chip icon={<WarningIcon />} label="Estoque baixo" color="warning" size="small" />;
    } else {
      return <Chip icon={<CheckCircleIcon />} label="Em estoque" color="success" size="small" />;
    }
  };

  const filteredProducts = products.filter(product => {
    // Filtro por status de estoque
    if (filter === 'low_stock' && product.quantity > 5) return false;
    if (filter === 'out_of_stock' && product.quantity > 0) return false;
    
    // Filtro por busca
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        product.name.toLowerCase().includes(term) ||
        product.description.toLowerCase().includes(term) ||
        product.eventTitle.toLowerCase().includes(term)
      );
    }
    
    return true;
  });

  const stats = {
    totalProducts: products.length,
    lowStock: products.filter(p => p.quantity <= 5 && p.quantity > 0).length,
    outOfStock: products.filter(p => p.quantity === 0).length,
    totalRevenue: products.reduce((sum, p) => sum + (p.price * 0), 0) // Placeholder
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Carregando produtos...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          🛒 Todos os Produtos
        </Typography>
        <Button
          variant="contained"
          component={Link}
          to="/dashboard/eventos"
          startIcon={<ShoppingCartIcon />}
        >
          Gerenciar Eventos
        </Button>
      </Box>

      {/* Alertas */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Estatísticas */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <ShoppingCartIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4">{stats.totalProducts}</Typography>
              <Typography variant="body2" color="text.secondary">
                Total de Produtos
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <WarningIcon color="warning" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4">{stats.lowStock}</Typography>
              <Typography variant="body2" color="text.secondary">
                Estoque Baixo
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <WarningIcon color="error" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4">{stats.outOfStock}</Typography>
              <Typography variant="body2" color="text.secondary">
                Sem Estoque
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <CheckCircleIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4">{stats.totalProducts - stats.lowStock - stats.outOfStock}</Typography>
              <Typography variant="body2" color="text.secondary">
                Em Estoque
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtros */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Buscar produtos"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Nome do produto, descrição ou evento..."
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status do Estoque</InputLabel>
              <Select
                value={filter}
                label="Status do Estoque"
                onChange={(e) => setFilter(e.target.value as any)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="low_stock">Estoque Baixo</MenuItem>
                <MenuItem value="out_of_stock">Sem Estoque</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography variant="body2" color="text.secondary" align="right">
              {filteredProducts.length} produto(s)
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabela de Produtos */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Lista de Produtos
          </Typography>
          
          {filteredProducts.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <ShoppingCartIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Nenhum produto encontrado
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchTerm || filter !== 'all' 
                  ? 'Tente ajustar os filtros de busca' 
                  : 'Crie produtos nos seus eventos para começar'
                }
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Produto</TableCell>
                    <TableCell>Evento</TableCell>
                    <TableCell>Preço</TableCell>
                    <TableCell>Estoque</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredProducts.map((product) => (
                    <TableRow key={product._id}>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {product.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {product.description}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {product.eventTitle}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight="bold">
                          R$ {product.price.toFixed(2).replace('.', ',')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2">
                          {product.quantity} unidades
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {getStockStatus(product.quantity)}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          component={Link}
                          to={`/${product.eventSlug}`}
                          size="small"
                          title="Ver evento público"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          component={Link}
                          to={`/dashboard/eventos/${product.eventId}/produtos`}
                          size="small"
                          title="Gerenciar produtos do evento"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default AllProductsPage; 